import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../helpers/contants';
import React, { useEffect, useRef } from 'react';
import { useAppSelector } from '../redux/store';

interface Props {
  children: React.ReactNode;
  isLoggedIn: boolean;
}

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

const PrivateRoute: React.JSX.Element = ({ children, isLoggedIn }: Props) => {
  const { token, isUserDetailsAdded } = useAppSelector(state => state.auth);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoggedIn && !isUserDetailsAdded) {
      navigate(ROUTES.SELECT_DEPARTMENT, { replace: true });
    }
  }, [isLoggedIn, isUserDetailsAdded]);

  // Tracks whether this route was ever reached while authenticated, so a
  // logout that happens while already sitting on a page (e.g. clicking
  // "Logout" from /scheduler) isn't mistaken for a deep-link bounce.
  const wasEverLoggedIn = useRef(isLoggedIn);
  useEffect(() => {
    if (isLoggedIn) {
      wasEverLoggedIn.current = true;
    }
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    const isDeepLinkBounce = !wasEverLoggedIn.current;

    if (isDeepLinkBounce) {
      sessionStorage.setItem(
        POST_LOGIN_REDIRECT_KEY,
        `${location.pathname}${location.search}`,
      );
    }
    // Marks this /login visit as an automatic bounce (as opposed to the
    // user manually navigating/clicking "Sign In", or logging out from a
    // page they were already on), so AuthLayout knows whether to keep or
    // clear the redirect saved above.
    return (
      <Navigate
        to={ROUTES.LOGIN}
        state={{ redirectedFromProtectedRoute: isDeepLinkBounce }}
      />
    );
  }

  return children;
};

export default PrivateRoute;
